import { cn } from '@rocco/utils/cn';
import { QUESTIONNAIRE_COLOUR } from '@rocco/components/questionnaire';
import type { BuildHubOption } from '~/ui-rocco/hooks/use-build-hubs';

import type { MatchAnswers } from '../answers';
import { describePlace } from './LocationStep';

export interface ProjectSummaryStepProps {
    answers: MatchAnswers;
    update: (patch: Partial<MatchAnswers>) => void;
    projects: BuildHubOption[];
}

/**
 * What the project is about to answer on the reader's behalf.
 *
 * This screen is the whole reason the questions can be skipped. Carrying a
 * project's details silently would mean a brief shaped by data the reader never
 * saw and may not recognise — a project half-filled a year ago would quietly
 * misdescribe what they are asking for now. Showing it makes the saving
 * something they agreed to.
 */
export const ProjectSummaryStep = ({
    answers,
    update,
    projects,
}: ProjectSummaryStepProps) => {
    const project = projects.find(
        (entry) => Number(entry.ID) === answers.buildHubID,
    );

    const rows: [string, string][] = [
        [
            'Location',
            answers.location ? describePlace(answers.location) : answers.region,
        ],
        ['Project type', answers.projectType],
        ['Budget', answers.budget],
    ].filter(([, value]) => Boolean(value)) as [string, string][];

    if (!answers.useProjectDetails) {
        return (
            <p
                className={cn(
                    'font-sans text-4 leading-[1.4]',
                    QUESTIONNAIRE_COLOUR.inkSubtle,
                )}
            >
                You&rsquo;ll answer these yourself. The brief is still filed
                against {project?.Title ?? 'your project'}.
            </p>
        );
    }

    return (
        <div className="flex flex-col gap-5">
            <ul className="overflow-hidden rounded-3">
                {rows.map(([label, value], index) => (
                    <li
                        className={cn(
                            'flex items-baseline justify-between gap-6 px-4.25 py-3.5',
                            QUESTIONNAIRE_COLOUR.surface,
                            index > 0 && 'mt-0.5',
                        )}
                        key={label}
                    >
                        <span
                            className={cn(
                                'shrink-0 font-sans text-3.5',
                                QUESTIONNAIRE_COLOUR.inkMuted,
                            )}
                        >
                            {label}
                        </span>
                        <span
                            className={cn(
                                'text-right font-sans text-4 leading-[1.3]',
                                QUESTIONNAIRE_COLOUR.ink,
                            )}
                        >
                            {value}
                        </span>
                    </li>
                ))}
            </ul>

            {rows.length === 0 ? (
                <p
                    className={cn(
                        'font-sans text-4 leading-[1.4]',
                        QUESTIONNAIRE_COLOUR.inkSubtle,
                    )}
                >
                    That project has no details filled in yet, so we&rsquo;ll
                    ask you for them.
                </p>
            ) : (
                <button
                    className={cn(
                        'self-start font-sans text-4 underline underline-offset-4',
                        QUESTIONNAIRE_COLOUR.inkSubtle,
                    )}
                    onClick={() => update({ useProjectDetails: false })}
                    type="button"
                >
                    I&rsquo;ll answer these myself
                </button>
            )}
        </div>
    );
};
